import axios from 'axios';
import { alzheimerClassifications, biomarkerData } from '../config/alzheimerConfig';

const API_BASE_URL = 'https://vitaldev.vitalinsights.in/admin/alzheimer-reports';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export const alzheimerReportService = {
  // Get report by appointment ID
  getReportByAppointmentId: async (appointmentId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/by-appointment/${appointmentId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching alzheimer report:', error);
      throw error;
    }
  },

  // Create new alzheimer report
  createReport: async (payload) => {
    try {
      const response = await axios.post(
        API_BASE_URL,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating alzheimer report:', error);
      throw error;
    }
  },

  // Update existing alzheimer report
  updateReport: async (reportId, payload) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/${reportId}`,
        payload,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating alzheimer report:', error);
      throw error;
    }
  },

  // Generate HTML for preview/PDF
  generateAlzheimerReportHTML: async (formData) => {
    try {
      // Fetch the HTML template
      const templateResponse = await fetch('/templates/alzheimer_report.html');
      let html = await templateResponse.text();

      // Helper function to determine status based on value and range
      const determineStatus = (name, value, providedStatus) => {
        // If status is explicitly provided, use it
        if (providedStatus) return providedStatus;
        
        // For MoCA, use provided status or default to High
        if (name === 'MoCA') return 'High';
        
        const biomarker = biomarkerData[name];
        if (!biomarker || !biomarker.maxRange || value === null || value === undefined) {
          return 'Normal';
        }
        
        const numValue = parseFloat(value);
        const maxRange = biomarker.maxRange;
        const mildElevationThreshold = maxRange * 1.2; // 20% above max range
        
        if (numValue <= maxRange) {
          return 'Normal';
        } else if (numValue <= mildElevationThreshold) {
          return 'Mild elevation';
        } else {
          return 'Elevated';
        }
      };

      // Helper function to get pill class for summary display
      const getSummaryPillClass = (status) => {
        if (status === 'Elevated') return 'pill-high';
        if (status === 'Mild elevation') return 'pill-elevated';
        if (status === 'High') return 'pill-normal';
        if (status === 'Low') return 'pill-low';
        return 'pill-normal';
      };

      // Helper function to get status display text for summary
      const getStatusDisplayText = (status) => {
        if (status === 'Mild elevation') return 'Borderline / High';
        if (status === 'High') return 'Normal';
        if (status === 'Low') return 'MCI / Abnormal';
        return status.toUpperCase();
      };

      // Helper function to generate disease classification and recommendations
      const generateDiseaseClassification = (pTau217Status, nflStatus, apoE4Status, mocaStatus) => {
        const biomarkerAbnormal = pTau217Status !== 'Normal' || nflStatus !== 'Normal' || apoE4Status !== 'Normal';
        const geneticRisk = apoE4Status !== 'Normal';
        const cognitiveNormal = mocaStatus === 'High';
        const cognitiveImpaired = mocaStatus === 'Low';
        
        let classificationData = alzheimerClassifications.noCognitiveRisk;

        if (biomarkerAbnormal && cognitiveNormal && !geneticRisk) {
          classificationData = alzheimerClassifications.preclinicalNormal;
        } else if (biomarkerAbnormal && cognitiveNormal && geneticRisk) {
          classificationData = alzheimerClassifications.preclinicalGeneticRisk;
        } else if (biomarkerAbnormal && cognitiveImpaired && !geneticRisk) {
          classificationData = alzheimerClassifications.mildCognitiveImpairment;
        } else if (biomarkerAbnormal && cognitiveImpaired && geneticRisk) {
          classificationData = alzheimerClassifications.prodromalAlzheimers;
        } else if (!biomarkerAbnormal && cognitiveNormal && !geneticRisk) {
          classificationData = alzheimerClassifications.noCognitiveRisk;
        } else if (!biomarkerAbnormal && cognitiveNormal && geneticRisk) {
          classificationData = alzheimerClassifications.geneticRisk;
        } else if (!biomarkerAbnormal && cognitiveImpaired && !geneticRisk) {
          classificationData = alzheimerClassifications.cognitiveImpairmentNoAD;
        } else if (!biomarkerAbnormal && cognitiveImpaired && geneticRisk) {
          classificationData = alzheimerClassifications.cognitiveImpairmentWithGeneticRisk;
        } else {
          classificationData = alzheimerClassifications.alzheimersDementia;
        }

        return {
          classification: classificationData.classification,
          recommendations: classificationData.recommendations
        };
      };

      // Helper function to get pill class based on status
      const getPillClass = (status) => {
        if (status === 'Elevated') return 'pill-high';
        if (status === 'Mild elevation') return 'pill-elevated';
        return 'pill-normal';
      };

      // Helper function to format biomarker rows
      const formatBiomarkerRow = (name, value, status, range) => {
        const statusForDisplay = determineStatus(name, value, status);
        const biomarkerInfo = biomarkerData[name];
        const interpretation = biomarkerInfo?.interpretations?.[statusForDisplay] || '';
        const standardRange = range || biomarkerInfo?.range || '';
        const pillClass = getPillClass(statusForDisplay);
        
        return `
          <tr>
            <td class="font-bold">${name}</td>
            <td class="td_middle"><span class="pill ${pillClass}">${statusForDisplay}</span></td>
            <td class="td_middle"><span class="value1">${value || ''}</span><br><span class="range">(${standardRange})</span></td>
            <td class="interpretation">${interpretation}</td>
          </tr>
        `;
      };

      // Helper function to format cognitive assessment (MoCA)
      const formatCognitiveAssessment = (mocaValue, mocaStatus, mocaRange) => {
        const statusForDisplay = mocaStatus || 'High';
        const mocaInfo = biomarkerData.MoCA;
        const interpretation = mocaInfo?.interpretations?.[statusForDisplay] || '';
        const standardRange = mocaRange || mocaInfo?.range || '0-30';
        const pillClass = getPillClass(statusForDisplay);
        
        return `
          <tr>
            <td class="font-bold">MoCA</td>
            <td class="td_middle"><span class="pill ${pillClass}">${statusForDisplay}</span></td>
            <td class="td_middle"><span class="value1">${mocaValue || ''}</span><br><span class="range">(${standardRange})</span></td>
            <td class="interpretation">${interpretation}</td>
          </tr>
        `;
      };

      // Helper function to format plasma biomarkers
      const formatPlasmaBiomarkers = (pTau217, pTau217Status, pTau217Range, nfl, nflStatus, nflRange, apoE4, apoE4Status, apoE4Range) => {
        let html = '';
        
        if (pTau217) {
          html += formatBiomarkerRow('pTau-217', pTau217, pTau217Status, pTau217Range);
        }
        
        if (nfl) {
          html += formatBiomarkerRow('NfL', nfl, nflStatus, nflRange);
        }
        
        if (apoE4) {
          html += formatBiomarkerRow('ApoE4', apoE4, apoE4Status, apoE4Range);
        }
        
        return html;
      };

      // Replace placeholders
      html = html.replaceAll('{{patient_name}}', formData.patient_name || '');
      html = html.replaceAll('{{patient_gender}}', formData.patient_gender || '');
      html = html.replaceAll('{{patient_age}}', formData.patient_age || '');
      html = html.replaceAll('{{exam_date}}', formData.exam_date || '');
      html = html.replaceAll('{{patient_phone}}', formData.patient_phone || '');

      // Replace cognitive assessment section
      const cognitiveHtml = formatCognitiveAssessment(formData.moca, formData.moca_status, formData.moca_range);
      html = html.replace('{{cognitive_assessment}}', cognitiveHtml);

      // Replace plasma biomarkers section
      const plasmaHtml = formatPlasmaBiomarkers(
        formData.pTau_217,
        formData.pTau_217_status,
        formData.pTau_217_range,
        formData.nfl,
        formData.nfl_status,
        formData.nfl_range,
        formData.apoE4,
        formData.apoE4_status,
        formData.apoE4_range
      );
      html = html.replace('{{plasma_biomarkers}}', plasmaHtml);

      // Generate summary section
      const pTau217Status = determineStatus('pTau-217', formData.pTau_217, formData.pTau_217_status);
      const nflStatus = determineStatus('NfL', formData.nfl, formData.nfl_status);
      const apoE4Status = determineStatus('ApoE4', formData.apoE4, formData.apoE4_status);
      const mocaStatus = formData.moca_status || 'High';

      const { classification, recommendations } = generateDiseaseClassification(pTau217Status, nflStatus, apoE4Status, mocaStatus);

      const summaryHtml = `
        <div class="report-summary">
            <div>pTau-217 <span class="pill ${getSummaryPillClass(pTau217Status)}">${getStatusDisplayText(pTau217Status)}</span></div>
            <p>+</p>
            <div>NfL <span class="pill ${getSummaryPillClass(nflStatus)}">${getStatusDisplayText(nflStatus)}</span></div>
            <p>+</p>
            <div>ApoE4 <span class="pill ${getSummaryPillClass(apoE4Status)}">${getStatusDisplayText(apoE4Status)}</span></div>
            <p>+</p>
            <div>MoCA <span class="pill ${getSummaryPillClass(mocaStatus)}">${getStatusDisplayText(mocaStatus)}</span></div>
            <p>=</p>
        </div>

        <div class="disease-name">
            <p>${classification}</p>
        </div>

        <div class="subtitle">Recommendation</div>
        <ul class="recommendation">
            ${recommendations.map(rec => `<li>${rec}</li>`).join('')}
        </ul>
      `;

      html = html.replace('{{summary_section}}', summaryHtml);

      return html;
    } catch (error) {
      console.error('Error generating HTML:', error);
      throw error;
    }
  }
};

// Export the HTML generation function separately for convenience
export const generateAlzheimerReportHTML = alzheimerReportService.generateAlzheimerReportHTML;
